import { useState, useEffect } from "react";
import { api } from "../api";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const STATO_COLOR = {
  bozza:          "#94a3b8",
  in_approvazione:"#f59e0b",
  approvato:      "#3b82f6",
  rifiutato:      "#ef4444",
  inviato:        "#06b6d4",
  accettato:      "#22c55e",
  scaduto:        "#f97316",
};
const fmt  = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtN = (n) => Number(n || 0).toLocaleString("it-IT");

function KpiCard({ label, valore, sub, color }) {
  return (
    <div style={{ background: "white", borderRadius: 12, padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: `4px solid ${color || "#0f2850"}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: "#0f2850" }}>{valore}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function BarChart({ data, labelKey, valueKey, color }) {
  if (!data?.length) return <div style={{ color: "#94a3b8", fontSize: 12, padding: "20px 0" }}>Nessun dato</div>;
  const max = Math.max(...data.map(d => d[valueKey] || 0));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
      {data.map((d, i) => {
        const h = max > 0 ? Math.max(4, ((d[valueKey] || 0) / max) * 100) : 4;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ width: "100%", height: `${h}%`, background: color || "#0f2850", borderRadius: "4px 4px 0 0", minHeight: 4 }} title={fmt(d[valueKey])} />
            <div style={{ fontSize: 9, color: "#94a3b8", textAlign: "center" }}>{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const [dati, setDati]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [anno, setAnno]     = useState(new Date().getFullYear());

  useEffect(() => {
    setLoading(true);
    api.getDashboard(anno)
      .then(d => { setDati(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [anno]);

  if (loading) return <div className="loading">Caricamento dashboard...</div>;
  if (!dati)   return <div className="errore-box">Errore caricamento dati</div>;

  const { kpi, perStato, perMese, perTipo, topVenditori, approvPendenti } = dati;
  const tassoConversione = kpi.totale_preventivi > 0
    ? ((kpi.accettati / kpi.totale_preventivi) * 100).toFixed(1)
    : "0.0";

  // Mappa mesi con nomi
  const datiMesi = MESI.map((nome, i) => {
    const mese = String(i + 1).padStart(2, "0");
    const found = perMese.find(m => m.mese === mese);
    return { nome, valore: found?.valore || 0, count: found?.count || 0 };
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {approvPendenti > 0 && (
            <span className="stato-badge" style={{ background: "#fef3c7", color: "#92400e", fontSize: 12 }}>
              ⏳ {approvPendenti} approvazioni pendenti
            </span>
          )}
          <select className="form-input" style={{ width: 100 }} value={anno} onChange={e => setAnno(parseInt(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <KpiCard label="Preventivi totali" valore={fmtN(kpi.totale_preventivi)} color="#0f2850" />
        <KpiCard label="Valore totale"     valore={fmt(kpi.valore_totale)}      color="#1d4ed8" />
        <KpiCard label="Valore medio"      valore={fmt(kpi.valore_medio)}       color="#7c3aed" />
        <KpiCard label="Tasso conversione" valore={`${tassoConversione}%`} sub={`${fmtN(kpi.accettati)} accettati`} color="#15803d" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* Andamento mensile */}
        <div className="card">
          <h2 className="card-title">📈 Andamento mensile {anno}</h2>
          <BarChart data={datiMesi} labelKey="nome" valueKey="valore" color="#0f2850" />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            {datiMesi.map((m, i) => (
              <div key={i} style={{ textAlign: "center", fontSize: 9, color: "#64748b" }}>
                {m.count > 0 && <div style={{ fontWeight: 700, color: "#0f2850" }}>{m.count}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Per stato */}
        <div className="card">
          <h2 className="card-title">📊 Preventivi per stato</h2>
          {perStato.length === 0 && <div style={{ color: "#94a3b8", fontSize: 12 }}>Nessun dato</div>}
          {perStato.map(s => (
            <div key={s.stato} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span className="stato-badge" style={{ background: STATO_COLOR[s.stato] + "22", color: STATO_COLOR[s.stato], minWidth: 110, textAlign: "center" }}>
                {s.stato}
              </span>
              <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, overflow: "hidden", height: 8 }}>
                <div style={{ height: "100%", background: STATO_COLOR[s.stato], width: `${Math.min(100, (s.count / kpi.totale_preventivi) * 100)}%`, borderRadius: 6 }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, minWidth: 24 }}>{s.count}</span>
              <span style={{ fontSize: 11, color: "#64748b", minWidth: 80, textAlign: "right" }}>{fmt(s.valore)}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

        {/* Per tipo cliente */}
        <div className="card">
          <h2 className="card-title">👤 Per tipo cliente</h2>
          {perTipo.map(t => (
            <div key={t.tipo_cliente} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
              <span style={{ fontWeight: 700 }}>{t.tipo_cliente === "DOM" ? "Domestico" : t.tipo_cliente === "AZ" ? "Aziendale" : "PA"}</span>
              <span>{t.count} preventivi</span>
              <span style={{ fontWeight: 700, color: "#0f2850" }}>{fmt(t.valore)}</span>
            </div>
          ))}
        </div>

        {/* Top venditori */}
        {topVenditori.length > 0 && (
          <div className="card">
            <h2 className="card-title">🏆 Top venditori</h2>
            {topVenditori.map((v, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: 16, minWidth: 24 }}>{["🥇","🥈","🥉"][i] || `${i+1}.`}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{v.nome}</span>
                <span style={{ fontSize: 12, color: "#64748b" }}>{v.count} prev.</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f2850" }}>{fmt(v.valore)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
